import GenericTable from 'modules/Tables'
import BarChart from 'modules/Tables/Dashboard/AllDashboards/BarChart'

import { IModel } from 'interfaces/model.interface'
import {
  INavigationPages,
  ISingleNavigationPage,
  defaultDashboardConfig,
} from 'interfaces/navigationPage.interface'

import { TreatmentsSlugs } from './TreatmentsMenu'
import {
  TreatmentClinicalTrialsModel,
  TreatmentMediaModel,
  TreatmentResultsApprovedCandidatesModel,
  TreatmentTweetsModel,
  TreatmentApprovalsOrRejectionsDealsModelByCountry,
  TreatmentCandidateRankingAntiviralsModel,
  TreatmentCandidateRankingModel,
  TreatmentPressReleasesModel,
  TreatmentApprovalsProphylaxis,
  TreatmentApprovalsOrRejectionsDealsModelByTreatment,
  TreatmentCandidateRankingPassiveImmunotherapiesModel,
  TreatmentCandidateLongCovidModel,
  TreatmentResultsLongCovidApprovedCandidatesModel,
} from './TreatmentsModels'
import barChartMessages from 'modules/Tables/Dashboard/AllDashboards/BarChart/message'

type TreatmentNavigationPage<TModel extends IModel<any>> =
  ISingleNavigationPage<TreatmentsSlugs, TModel>

const Candidates: TreatmentNavigationPage<
  typeof TreatmentCandidateRankingModel
> = {
  key: 'Candidates',
  path: ['candidates', 'dashboard'],
  component: GenericTable,
  model: {
    ...TreatmentCandidateRankingModel,
    name: 'All Candidates',
    newNote: {
      text: barChartMessages,
      newUntil: new Date(2024, 8, 25),
    },
  },
  views: [
    {
      name: 'All',
      airtableName: 'covid_treatments_all',
      airtableBase: 'lzdb',
    },
    {
      name: 'Antivirals',
      airtableName: 'covid_treatments_antivirals',
      airtableBase: 'lzdb',
      overrideModel: TreatmentCandidateRankingAntiviralsModel,
    },
    {
      name: 'Antibodies',
      airtableName: 'covid_treatments_antibodies',
      airtableBase: 'lzdb',
      overrideModel: TreatmentCandidateRankingPassiveImmunotherapiesModel,
    },
    {
      name: 'Anti Inflammatory',
      airtableName: 'covid_treatments_anti_inflammatory',
      airtableBase: 'lzdb',
      overrideModel: {
        ...TreatmentCandidateRankingModel,
        name: 'Anti Inflammatory/Immunomodulatory',
      },
    },
    {
      name: 'Long Covid',
      airtableName: 'covid_treatments_long_covid',
      airtableBase: 'lzdb',
      overrideModel: {
        ...TreatmentCandidateLongCovidModel,
        name: 'Long Covid',
      },
    },
  ],
  autoGenerateDashboard: {
    ...defaultDashboardConfig,
    render: BarChart,
    verticalAxisLabel: 'Number of Candidates',
    filterBlacklistOptions: [
      'alternativeNames',
      'activeDevelopment',
      'researchInstitutions',
      'approvalCountry',
    ],
    defaultHorizontalFilterValue: {
      label: 'Phase',
      value: 'latestPhaseManual',
    },
    defaultStackByFilterValue: {
      label: 'Technology Type',
      value: 'technologyType',
    },
    sortingOrders: {
      lookupAfTrClinicalTrialStageFromBnf: [
        'Preclinical',
        'Phase 0',
        'Phase I',
        'Phase I/II',
        'Phase II',
        'Phase II/III',
        'Phase III',
        'Approved',
        'Phase IV',
        'Discontinued',
        'Paused',
        'Inactive',
        'Not Applicable',
        'Unknown',
      ],
    },
  },
}

const ClinicalTrials: TreatmentNavigationPage<
  typeof TreatmentClinicalTrialsModel
> = {
  key: 'ClinicalTrials',
  path: ['trials', 'table'],
  component: GenericTable,
  model: TreatmentClinicalTrialsModel,
  views: [
    {
      name: 'Default',
      airtableName: 'api_app_treatments',
      airtableBase: 'idtreatments360',
    },
    {
      name: 'Long Covid',
      airtableName: 'FE Long COVID Trials table',
      airtableBase: 'idtreatments360',
    },
  ],
}

const ResultsApprovedCandidates: TreatmentNavigationPage<
  typeof TreatmentResultsApprovedCandidatesModel
> = {
  key: 'ResultsApprovedCandidates',
  path: ['results', 'table'],
  component: GenericTable,
  model: TreatmentResultsApprovedCandidatesModel,
  views: [
    {
      name: 'Default',
      airtableName: 'FE view for COVID trial results',
      airtableBase: 'idtreatments360',
    },
    {
      name: 'Long Covid',
      airtableName: 'FE view for Long COVID results',
      overrideModel: TreatmentResultsLongCovidApprovedCandidatesModel,
      airtableBase: 'idtreatments360',
    },
  ],
}

const ApprovalsOrRejectionsByTreatment: TreatmentNavigationPage<
  typeof TreatmentApprovalsOrRejectionsDealsModelByTreatment
> = {
  key: 'ApprovalsOrRejectionsByTreatment',
  path: ['regulatory', 'by-treatment'],
  model: TreatmentApprovalsOrRejectionsDealsModelByTreatment,
  component: GenericTable,
  views: [
    {
      name: 'All',
      airtableName: 'API-treatments-approvals',
      airtableBase: 'idtreatments360',
    },
    {
      name: 'Prophylaxis',
      airtableName: 'API - C-19 Tx Approvals Prophylaxis',
      airtableBase: 'idtreatments360',
      overrideModel: TreatmentApprovalsProphylaxis,
    },
    {
      name: 'Outpatient',
      airtableName: 'API - C-19 Tx Approvals Outpatient',
      airtableBase: 'idtreatments360',
      overrideModel: TreatmentApprovalsProphylaxis,
    },
    {
      name: 'Inpatient',
      airtableName: 'API - C-19 Tx Approvals Inpatient',
      airtableBase: 'idtreatments360',
      overrideModel: TreatmentApprovalsProphylaxis,
    },
  ],
}

const ApprovalsOrRejectionsByCountry: TreatmentNavigationPage<
  typeof TreatmentApprovalsOrRejectionsDealsModelByCountry
> = {
  key: 'ApprovalsOrRejectionsByCountry',
  path: ['regulatory', 'by-country'],
  model: TreatmentApprovalsOrRejectionsDealsModelByCountry,
  component: GenericTable,
  views: [
    {
      name: 'Default',
      airtableName: 'API-treatments-approvals',
      airtableBase: 'idtreatments360',
    },
  ],
}

const PressReleases: TreatmentNavigationPage<
  typeof TreatmentPressReleasesModel
> = {
  key: 'PressReleases',
  model: TreatmentPressReleasesModel,
  path: null,
  component: GenericTable,
  views: [
    {
      name: 'Default',
      airtableName: 'api_app_treatments',
      airtableBase: 'idtreatments360',
    },
  ],
}

const Media: TreatmentNavigationPage<typeof TreatmentMediaModel> = {
  key: 'Media',
  model: TreatmentMediaModel,
  path: null,
  component: GenericTable,
  views: [
    {
      name: 'Default',
      airtableName: 'api_app_treatments',
      airtableBase: 'idtreatments360',
    },
  ],
}

const Tweets: TreatmentNavigationPage<typeof TreatmentTweetsModel> = {
  key: 'Tweets',
  path: null,
  component: GenericTable,
  model: TreatmentTweetsModel,
  views: [
    {
      name: 'Default',
      airtableName: 'api_app_treatments',
      airtableBase: 'idtreatments360',
    },
  ],
}

export const TreatmentsPages: INavigationPages<TreatmentsSlugs> = {
  Candidates,
  ClinicalTrials,
  ResultsApprovedCandidates,
  ApprovalsOrRejectionsByTreatment,
  ApprovalsOrRejectionsByCountry,
  PressReleases,
  Media,
  Tweets,
}
